"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Facebook, Twitter, Instagram } from "lucide-react";
export default function ContactTicketPurchase() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="contact-ticket-purchase" ref={ref} className="py-16 px-4 bg-light-background text-foreground-light">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12">
          {}
          <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Get in Touch
            </h2>
            <FormProvider className="space-y-6" formId="contact-form-contact-ticket-purchase" sectionName="contact-ticket-purchase" defaultValues={{
            name: "",
            email: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you soon.">
              <ContactFormFields />
            </FormProvider>
          </div>

          {}
          <div className={`flex flex-col justify-center items-start transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          animationDelay: "100ms"
        }}>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Experience The Mass
            </h2>
            <p className="text-lg mb-8 text-foreground-light/80">
              Secure your seat for an unforgettable theatrical journey. Limited tickets available.
            </p>
            <Button className="btn-primary text-lg px-8 py-6 mb-8 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-105 hover:shadow-medium active:scale-100" asChild>
              <Link to="#" aria-label="Purchase tickets for The Mass">
                Purchase Tickets
              </Link>
            </Button>

            {}
            <div className="flex gap-6">
              <Link to="#" aria-label="Follow us on Facebook" className="text-foreground-light hover:text-primary transition-colors duration-[var(--duration-fast)]">
                <Facebook className="size-6" />
              </Link>
              <Link to="#" aria-label="Follow us on Twitter" className="text-foreground-light hover:text-primary transition-colors duration-[var(--duration-fast)]">
                <Twitter className="size-6" />
              </Link>
              <Link to="#" aria-label="Follow us on Instagram" className="text-foreground-light hover:text-primary transition-colors duration-[var(--duration-fast)]">
                <Instagram className="size-6" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary/20 rounded-lg">
        <p className="text-foreground-light font-semibold">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary transition-all duration-[var(--duration-medium)] hover:bg-primary-hover">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}